import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
/**
 * PracticeSession — Core practice/assessment session orchestrator.
 *
 * DDD co-locatie: moved from components/practice/ in s69.
 * Optimized: extracted 7 full-page states + exit modal → practiceSessionStates.tsx (~200r).
 * Reduced from 978→~680 lines.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { type Question, type ModuleId, getQuestionTypeConfig, getLearningGoal, getFamilyDefinition } from '../../content';
import { SessionMode } from '../../app/types';
import type { FamilyMasteryState } from '../../mastery';
import { getAdaptiveSessionV11 } from '../../mastery';
import { Button } from '../../components/ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { AnswerComparison } from './feedback/AnswerComparison';
import { formatCorrectAnswer, formatUserAnswer } from './feedback/feedbackAnswerFormatters';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { SessionSummary } from './SessionSummary';
import { SessionLoadingState, SessionErrorState, SessionEmptyNoContent, SessionEmptyAllMastered, SessionEmptyFallback, SessionSafetyError, ExitConfirmModal } from './practiceSessionStates';
import { getMotivationalMessage, getStreakMessage, getLevelTitle } from '../../utils/motivationalMessages';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG, XP_REWARDS, calculateXP } from '../../config/constants';
import { ArrowRight, CheckCircle2, Star } from 'lucide-react';
import { validateSessionAvailability } from '../logic/sessionValidation';
import { debug, warn, error as logError } from '../../utils/logger';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { DjayAvatar, useDjaySelection, getDjayHelpRequestPrompt } from '../../mascot';
import { createMasteryQueries } from '../../accounts/hooks/useLearnerActions';
import { isFastResponse } from '../logic/questionTimeBaselines';
// ── Types ────────────────────────────────────────────────────
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number, timeMs?: number, helpRequested?: boolean, skipped?: boolean) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  mode?: SessionMode;
  isFluency?: boolean;
  labMode?: string;
  pendingLevelUp?: number | null;
  xp?: number;
  onClearPendingLevelUp?: () => void;
}
type Phase = 'question' | 'feedback' | 'summary' | 'fluency-correct' | 'fluency-incorrect';
interface SessionAnswer {
  questionId: string;
  isCorrect: boolean;
  familyId?: string;
  questionType: string;
  errorType?: string;
  timeMs?: number;
}
// ── Helpers ──────────────────────────────────────────────────
function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false;
  return getQuestionTypeConfig(question.questionType || 'multiple_choice').isInteractive;
}
/** Reset all answer/session state to initial values. */
function getResetState() {
  return {
    currentQuestionIndex: 0,
    phase: 'question' as Phase,
    selectedOption: null as number | null,
    userAnswerIndex: null as number | null,
    userTypedAnswer: '',
    actualIsCorrect: null as boolean | null,
    interactiveReady: false,
    shouldValidateInteractive: false,
    currentStreak: 0,
    totalXpGained: 0
  };
}
// ── Component ────────────────────────────────────────────────
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  mode = 'practice',
  isFluency = false,
  labMode,
  pendingLevelUp = null,
  xp = 0,
  onClearPendingLevelUp
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Mode flags
  const isAssessmentMode = mode === 'assessment';
  const isChallenge = labMode === 'challenge';
  const showFeedback = !isAssessmentMode;
  const showStreak = !isAssessmentMode;
  const showCheatSheet = !isAssessmentMode && !isCrossModule;
  // Session state
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const lastGroupVersionRef = useRef(groupVersion);
  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  // UI state
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [cheatSheetMode, setCheatSheetMode] = useState<'cheatsheet' | 'help-request'>('cheatsheet');
  const [helpRequested, setHelpRequested] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const isSkippedRef = useRef(false);
  // Stats (disabled in assessment mode)
  const [currentStreak, setCurrentStreak] = useState(0);
  const [speedStreak, setSpeedStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [totalXpGained, setTotalXpGained] = useState(0);
  // Celebration state
  const [streakMilestone, setStreakMilestone] = useState<{
    count: number;
    message: string;
  } | null>(null);
  const [dailyGoalReached, setDailyGoalReached] = useState(false);
  // Session tracking for summary
  const [sessionAnswers, setSessionAnswers] = useState<SessionAnswer[]>([]);
  const [preFamilyMastery, setPreFamilyMastery] = useState<Record<string, FamilyMasteryState>>({});
  const questionStartTimeRef = useRef<number>(Date.now());
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const answerProcessedRef = useRef(false);
  // Fluency elapsed timer (visible per-question stopwatch)
  const [elapsedMs, setElapsedMs] = useState(0);
  // Challenge adaptivity — C4: 3× wrong → tier downgrade encouragement
  const challengeConsecutiveErrorsRef = useRef(0);
  const [challengeTierDowngraded, setChallengeTierDowngraded] = useState(false);
  // Cheat sheet nudge — subtle pulse on button after 3 consecutive errors, max 1× per session
  const [cheatSheetNudge, setCheatSheetNudge] = useState(false);
  const nudgeFiredRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  // ── Reset helper ─────────────────────────────────────────
  const resetSession = () => {
    const r = getResetState();
    setSessionQuestions([]);
    setSessionInitialized(false);
    setCurrentQuestionIndex(r.currentQuestionIndex);
    setPhase(r.phase);
    setSelectedOption(r.selectedOption);
    setUserAnswerIndex(r.userAnswerIndex);
    setUserTypedAnswer(r.userTypedAnswer);
    setActualIsCorrect(r.actualIsCorrect);
    setInteractiveReady(r.interactiveReady);
    setShouldValidateInteractive(r.shouldValidateInteractive);
    setCurrentStreak(r.currentStreak);
    setSpeedStreak(0);
    setSessionAnswers([]);
    setTotalXpGained(r.totalXpGained);
    // Reset nudge + challenge state
    setCheatSheetNudge(false);
    setCheatSheetMode('cheatsheet');
    setHelpRequested(false);
    nudgeFiredRef.current = false;
    consecutiveErrorsRef.current = 0;
    challengeConsecutiveErrorsRef.current = 0;
    isSkippedRef.current = false;
    setChallengeTierDowngraded(false);
    clearTimeout(autoAdvanceTimerRef.current);
    answerProcessedRef.current = false;
  };
  // ── Escape key ───────────────────────────────────────────
  const handleEscapePress = useCallback(() => {
    if (isAssessmentMode) setShowExitConfirm(true);else onExit();
  }, [onExit, isAssessmentMode]);
  useEscapeKey(handleEscapePress, !isCheatSheetOpen);
  // ── Session initialization (adaptive engine) ─────────────
  useEffect(() => {
    if (!isInitialized) return;
    // Detect group change mid-session
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion;
      resetSession();
      debug('Session fully reset due to group change');
    }
    if (!sessionInitialized) {
      setInitError(null);
      // Capture pre-session mastery (deep clone)
      if (state.familyMastery) {
        try {
          setPreFamilyMastery(JSON.parse(JSON.stringify(state.familyMastery)));
        } catch (err) {
          logError('Failed to clone familyMastery:', err);
          setPreFamilyMastery({});
        }
      }
      if (isCrossModule) {
        setSessionQuestions(questions);
        setSessionInitialized(true);
      } else if (moduleId) {
        try {
          const familyMastery = state.familyMastery ?? {};
          const questionStates = state.questionStates ?? {};
          const recentFamilies = Object.values(familyMastery).filter(fm => fm && (fm.lastSeen ?? 0) > 0).sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0)).slice(0, 5).map(fm => fm.familyId).filter(id => id && id.length > 0);
          const recentQuestions = Object.entries(questionStates).filter(([_, qs]) => qs && (qs.lastAttemptDate ?? 0) > 0).sort((a, b) => (b[1]?.lastAttemptDate ?? 0) - (a[1]?.lastAttemptDate ?? 0)).slice(0, 10).map(([qId]) => qId.replace(/_g\d+$/, '')).filter(qId => qId && qId.length > 0);
          debug(`v11.2: Generating session for ${moduleId} | Group ${selectedGroup} | Families tracked: ${Object.keys(familyMastery).length}`);
          // Get starting tier for cold start boost
          const queries = createMasteryQueries(state, selectedGroup, manualPeriod);
          const startingTier = queries.getStartingLevel(moduleId, selectedGroup);
          const adaptiveSession = getAdaptiveSessionV11(moduleId, familyMastery, selectedGroup, questionStates, SESSION_CONFIG.QUESTIONS_PER_SESSION, recentFamilies, recentQuestions, undefined, startingTier);
          if (adaptiveSession.length === 0) {
            warn('Empty session generated - all content mastered or unavailable');
            setInitError('empty_session');
          }
          setSessionQuestions(adaptiveSession);
          setSessionInitialized(true);
          debug(`Session: ${adaptiveSession.length} questions for ${moduleId} (group ${selectedGroup}, engine: v11.2, mode: ${mode})`);
        } catch (error) {
          logError('Failed to generate session:', error);
          setInitError('generation_failed');
          setSessionQuestions([]);
          setSessionInitialized(true);
        }
      }
    }
  }, [moduleId, isCrossModule, questions, sessionInitialized, selectedGroup, groupVersion, isInitialized, state.familyMastery, state.questionStates, mode]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Reset answer state when question changes
  useEffect(() => {
    if (!currentQuestion) return;
    setSelectedOption(null);
    setUserAnswerIndex(null);
    setUserTypedAnswer('');
    setInteractiveReady(false);
    setShouldValidateInteractive(false);
    setHelpRequested(false);
    setCheatSheetMode('cheatsheet');
    questionStartTimeRef.current = Date.now();
    answerProcessedRef.current = false;
    isSkippedRef.current = false;
  }, [currentQuestion]);
  // Fluency elapsed timer — updates every 100ms during question phase
  useEffect(() => {
    if (!isFluency || phase !== 'question') {
      setElapsedMs(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - questionStartTimeRef.current);
    }, 100);
    return () => clearInterval(interval);
  }, [isFluency, phase, currentQuestionIndex]);
  // Enter key handler
  useEffect(() => {
    if (!currentQuestion) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key === 'Enter') {
        const interactive = isInteractiveQuestion(currentQuestion);
        if (interactive && interactiveReady) submitAnswer();else if (!interactive && userAnswerIndex !== null) submitAnswer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  // ── Early returns (full-page states) ─────────────────────
  if (!isInitialized || !sessionInitialized) return <SessionLoadingState isAssessmentMode={isAssessmentMode} data-id="element-6798" />;
  if (initError === 'generation_failed') return <SessionErrorState isAssessmentMode={isAssessmentMode} onExit={onExit} data-id="element-6799" />;
  if (sessionInitialized && sessionQuestions.length === 0) {
    const validationReport = moduleId ? validateSessionAvailability(moduleId, selectedGroup) : null;
    const hasContent = validationReport?.isValid ?? false;
    const hasAnyProgress = state.familyMastery && Object.keys(state.familyMastery).some(fId => state.familyMastery![fId].attempts > 0);
    if (!hasContent) return <SessionEmptyNoContent onExit={onExit} data-id="element-6800" />;
    if (hasAnyProgress) return <SessionEmptyAllMastered selectedGroup={selectedGroup} onExit={onExit} data-id="element-6801" />;
    return <SessionEmptyFallback isAssessmentMode={isAssessmentMode} onExit={onExit} data-id="element-6802" />;
  }
  if (!currentQuestion && sessionQuestions.length > 0) {
    logError('Current question is undefined but session has questions. Index:', currentQuestionIndex);
    return <SessionSafetyError onExit={onExit} data-id="element-6803" />;
  }
  // ── Answer processing ────────────────────────────────────
  const processAnswer = (isCorrect: boolean, distractorIndex: number, errorType?: string, delta?: number, payload?: string, isSkipped: boolean = false) => {
    if (answerProcessedRef.current) return;
    answerProcessedRef.current = true;
    if (isSkipped) isSkippedRef.current = true;
    const timeMs = Date.now() - questionStartTimeRef.current;
    setStreakMilestone(null);
    setDailyGoalReached(false);
    if (!isSkipped) {
      if (isFluency) {
        const questionType = currentQuestion.questionType || 'multiple_choice';
        if (isCorrect && isFastResponse(questionType, timeMs)) {
          setSpeedStreak(prev => prev + 1);
        } else {
          setSpeedStreak(0);
        }
      }
      // Challenge adaptivity — C4: track consecutive errors
      if (isChallenge) {
        if (!isCorrect) {
          challengeConsecutiveErrorsRef.current++;
          if (challengeConsecutiveErrorsRef.current >= 3 && !challengeTierDowngraded) {
            setChallengeTierDowngraded(true);
            // Auto-dismiss after 4 seconds
            setTimeout(() => setChallengeTierDowngraded(false), 4000);
          }
        } else {
          challengeConsecutiveErrorsRef.current = 0;
        }
      }
      // Track consecutive errors for cheat sheet nudge (subtle pulse, max 1× per session)
      if (!isCorrect) {
        consecutiveErrorsRef.current++;
        if (consecutiveErrorsRef.current >= 3 && !nudgeFiredRef.current && showCheatSheet) {
          setCheatSheetNudge(true);
          nudgeFiredRef.current = true;
        }
      } else {
        consecutiveErrorsRef.current = 0;
      }
      if (!isAssessmentMode) {
        const earned = calculateXP(isCorrect, currentQuestion.tier);
        setXpGained(earned);
        setTotalXpGained(prev => prev + earned);
        const questionsAnsweredToday = (state.dailyStreak?.todayQuestionsAnswered || 0) + 1;
        const dailyGoal = state.dailyStreak?.dailyGoalTarget || SESSION_CONFIG.DAILY_GOAL;
        if (questionsAnsweredToday === dailyGoal) setDailyGoalReached(true);
        if (isCorrect) {
          const newStreak = currentStreak + 1;
          setCurrentStreak(newStreak);
          const message = getStreakMessage(newStreak);
          if (message) setStreakMilestone({
            count: newStreak,
            message
          });
        } else {
          setCurrentStreak(0);
        }
        setMotivationalText(getMotivationalMessage({
          isCorrect,
          streak: currentStreak,
          questionNumber: currentQuestionIndex + 1,
          totalQuestions: sessionQuestions.length
        }));
      }
    }
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex, payload, errorType, delta, timeMs, helpRequested, isSkipped);
    setSessionAnswers(prev => [...prev, {
      questionId: currentQuestion.id,
      isCorrect,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice',
      errorType,
      timeMs
    }]);
    setSelectedOption(null);
    if (isSkipped) {
      // If skipped, immediately go to next question without feedback
      nextQuestion();
    } else if (isAssessmentMode) {
      autoAdvanceTimerRef.current = setTimeout(() => nextQuestion(), 600);
    } else if (isFluency && isCorrect) {
      setPhase('fluency-correct');
      autoAdvanceTimerRef.current = setTimeout(() => nextQuestion(), 1500);
    } else if (isFluency && !isCorrect) {
      setPhase('fluency-incorrect');
    } else {
      setPhase('feedback');
    }
  };
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    const config = getQuestionTypeConfig(currentQuestion.questionType || 'multiple_choice');
    const validation = config.validateAnswer(userAnswer, currentQuestion);
    if (validation.displayAnswer) setUserTypedAnswer(validation.displayAnswer);
    setActualIsCorrect(validation.isCorrect);
    processAnswer(validation.isCorrect, validation.distractorIndex ?? 0, validation.errorType, validation.delta, userAnswer);
  };
  const handleRendererAnswer = (userAnswer: string | number) => {
    if (isInteractiveQuestion(currentQuestion)) handleInteractiveComplete(userAnswer as string);else {
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  const submitAnswer = () => {
    if (isInteractiveQuestion(currentQuestion)) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    processAnswer(userAnswerIndex === currentQuestion.correctAnswer, userAnswerIndex);
  };
  const nextQuestion = () => {
    clearTimeout(autoAdvanceTimerRef.current);
    // Hard guard: in assessment mode, NEVER advance without a recorded answer
    if (isAssessmentMode && !answerProcessedRef.current) return;
    if (onClearPendingLevelUp && pendingLevelUp) onClearPendingLevelUp();
    // If skipped, we want to re-queue the question at the end of the session
    // so the student still has to answer it eventually.
    if (isSkippedRef.current) {
      setSessionQuestions(prev => [...prev, currentQuestion]);
    }
    if (isLastQuestion && !isSkippedRef.current) {
      if (isAssessmentMode) {
        onComplete({
          answeredCount: sessionQuestions.length
        });
        return;
      }
      setPhase('summary');
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    // Reset mode back to default after closing
    setTimeout(() => setCheatSheetMode('cheatsheet'), 300);
    if (!cheatSheetViewed) onCheatSheetView();
  };
  const handleHelpRequest = () => {
    setHelpRequested(true);
    setCheatSheetMode('help-request');
    setIsCheatSheetOpen(true);
  };
  const handleSkipQuestion = () => {
    setIsCheatSheetOpen(false);
    processAnswer(false, -1, undefined, undefined, undefined, true);
  };
  // ── Summary phase ────────────────────────────────────────
  if (phase === 'summary') {
    return <SessionSummary moduleId={moduleId!} sessionQuestions={sessionQuestions} sessionAnswers={sessionAnswers} preFamilyMastery={preFamilyMastery} isFluency={isFluency} onExit={() => onComplete({
      answeredCount: sessionQuestions.length
    })} onPracticeAgain={resetSession} xpGained={totalXpGained} data-id="element-6804" />;
  }
  if (!currentQuestion) return <SessionLoadingState isAssessmentMode={isAssessmentMode} data-id="element-6805" />;
  // ── Main render ──────────────────────────────────────────
  return <div className={`min-h-screen bg-[#FAFAF8] flex flex-col font-sans ${isAssessmentMode ? 'h-screen overflow-hidden' : ''}`} data-id="element-6806">
      {showCheatSheet && <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} familyId={currentQuestion.familyId} mode={cheatSheetMode} onSkip={handleSkipQuestion} data-id="element-6807" />}

      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} speedStreak={speedStreak} isFluency={isFluency} elapsedMs={isFluency && phase === 'question' ? elapsedMs : undefined} onExit={() => {
      if (isAssessmentMode) setShowExitConfirm(true);else onExit();
    }} onToggleCheatSheet={showCheatSheet ? () => {
      setCheatSheetMode('cheatsheet');
      setIsCheatSheetOpen(true);
      setCheatSheetNudge(false);
    } : undefined} isCheatSheetOpen={isCheatSheetOpen} nudgeActive={cheatSheetNudge} learningGoal={!isAssessmentMode && currentQuestion ? getLearningGoal(currentQuestion.familyId) : undefined} data-id="element-6808" />

      <div className={`flex-1 max-w-4xl mx-auto w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 flex flex-col ${isAssessmentMode ? 'min-h-0 overflow-y-auto pb-8' : 'min-h-[calc(100vh-64px)] justify-center'} ${isFluency ? 'bg-amber-50/20' : ''}`} data-id="element-6809">
        {/* Challenge tier-downgrade encouragement banner — C4 */}
        <AnimatePresence data-id="element-6810">
          {challengeTierDowngraded && isChallenge && <motion.div key="challenge-downgrade" initial={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 350,
          damping: 30
        }} className="mb-4 flex items-center gap-3" data-id="element-6811">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="flex-shrink-0" data-id="element-6812">
                <DjayAvatar size="md" emotion="encouraging" variant={selectedVariant} animate data-id="element-6813" />
              </motion.div>
              <div className="relative flex-1 max-w-[320px]" data-id="element-6814">
                <div className="bg-violet-50 rounded-2xl shadow-lg border border-violet-200 px-4 py-3" data-id="element-6815">
                  <p className="text-violet-900 font-bold text-sm leading-relaxed" data-id="element-6816">
                    Lastige vragen! 💪
                  </p>
                  <p className="text-violet-700 text-sm mt-1" data-id="element-6817">
                    Even terug naar de basis — dan bouwen we weer op.
                  </p>
                </div>
                <div className="absolute left-0 top-3 w-0 h-0 -ml-2 border-t-[8px] border-b-[8px] border-r-[10px] border-t-transparent border-b-transparent border-r-violet-50" aria-hidden="true" data-id="element-6818" />
              </div>
            </motion.div>}
        </AnimatePresence>

        <AnimatePresence mode="wait" data-id="element-6819">
          {phase === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
          opacity: 0,
          y: 20,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20,
          scale: 0.98
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="w-full" data-id="element-6820">
              <div data-id="element-6821">
                {helpRequested && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-4 flex justify-center" data-id="element-6822">
                    <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200 flex items-center gap-3" data-id="element-6823">
                      <DjayAvatar size="sm" emotion="encouraging" variant={selectedVariant} data-id="element-6824" />
                      <p className="text-sm font-bold text-slate-700" data-id="element-6825">
                        {getDjayHelpRequestPrompt().text}
                      </p>
                    </div>
                  </motion.div>}
                <QuestionCardClean question={currentQuestion} isFluency={isFluency} onHelpRequest={showCheatSheet && !helpRequested ? handleHelpRequest : undefined} data-id="element-6826">
                  <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} onSkipQuestion={isAssessmentMode ? undefined : nextQuestion} data-id="element-6827" />
                </QuestionCardClean>
              </div>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-6828">
                <Button size="xl" disabled={isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="w-full sm:w-auto sm:min-w-[280px] md:min-w-[360px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 rounded-2xl text-fluid-lg" data-id="element-6829">
                  <span className="mr-2 font-black" data-id="element-6830">Controleren</span>
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-6831" />
                </Button>
              </motion.div>
            </motion.div>}

          {phase === 'fluency-correct' && <motion.div key={`fluency-correct-${currentQuestionIndex}`} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex-1 flex flex-col items-center justify-center py-12" data-id="element-6832">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-8 md:p-12 text-center shadow-xl shadow-amber-100/50 max-w-md w-full relative" data-id="element-6833">
                {speedStreak >= 3 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg" data-id="element-6834">
                    {speedStreak}x Snelheidsreeks! ⚡
                  </motion.div>}
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              bounce: 0.5,
              delay: 0.1
            }} className="mx-auto mb-6 flex justify-center" data-id="element-6835">
                  <DjayAvatar size="lg" emotion="celebrating" variant={selectedVariant} data-id="element-6836" />
                </motion.div>
                <h2 className="text-2xl font-black text-amber-800 mb-2" data-id="element-6837">
                  {speedStreak >= 3 ? 'Lekker bezig! ⚡' : speedStreak > 0 ? 'Razendsnel! 🔥' : 'Goed gedaan!'}
                </h2>
                {xpGained > 0 && <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-amber-100 shadow-sm mt-4" data-id="element-6838">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-6839" />
                    <span className="font-bold text-amber-700" data-id="element-6840">
                      +{xpGained} XP
                    </span>
                  </div>}
              </div>
            </motion.div>}

          {phase === 'fluency-incorrect' && <motion.div key={`fluency-incorrect-${currentQuestionIndex}`} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="flex-1 flex flex-col items-center justify-center py-8 px-4" data-id="element-6841">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-6 md:p-8 shadow-xl shadow-amber-100/50 max-w-lg w-full" data-id="element-6842">
                <div className="flex items-center gap-4 mb-6" data-id="element-6843">
                  <div className="w-16 h-16 flex-shrink-0" data-id="element-6844">
                    <DjayAvatar size="md" emotion="encouraging" variant={selectedVariant} data-id="element-6845" />
                  </div>
                  <div className="bg-white rounded-2xl p-3 border border-amber-100 shadow-sm relative" data-id="element-6846">
                    <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-amber-100 rotate-45" data-id="element-6847" />
                    <p className="font-bold text-amber-900 text-sm md:text-base relative z-10" data-id="element-6848">
                      Oeps! Dat was niet helemaal goed. Kijk even naar het
                      juiste antwoord.
                    </p>
                  </div>
                </div>

                <div className="mb-8" data-id="element-6849">
                  <AnswerComparison userAnswer={formatUserAnswer(currentQuestion, isInteractiveQuestion(currentQuestion) ? userTypedAnswer : currentQuestion.options?.[userAnswerIndex ?? 0] || '')} correctAnswer={formatCorrectAnswer(currentQuestion)} isCorrect={false} mode="compact" data-id="element-6850" />
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 justify-end" data-id="element-6851">
                  <Button variant="ghost" onClick={() => setPhase('feedback')} className="w-full sm:w-auto text-amber-700 hover:text-amber-900 hover:bg-amber-100/50" data-id="element-6852">
                    Meer uitleg
                  </Button>
                  <Button variant="primary" onClick={nextQuestion} className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 border-amber-600 text-white shadow-md shadow-amber-500/20" data-id="element-6853">
                    Volgende
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-6854" />
                  </Button>
                </div>
              </div>
            </motion.div>}

          {phase === 'feedback' && showFeedback && <motion.div key={`feedback-${currentQuestionIndex}`} initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-full" data-id="element-6855">
              <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractiveQuestion(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractiveQuestion(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options[userAnswerIndex] : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode={isFluency ? 'compact' : 'full'} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} levelUp={pendingLevelUp ? {
            level: pendingLevelUp,
            xp,
            title: getLevelTitle(pendingLevelUp)
          } : null} streakMilestone={streakMilestone} dailyGoalReached={dailyGoalReached} onClearLevelUp={onClearPendingLevelUp} data-id="element-6856" />
            </motion.div>}
        </AnimatePresence>
      </div>

      <ExitConfirmModal show={showExitConfirm} currentIndex={currentQuestionIndex} totalQuestions={sessionQuestions.length} onContinue={() => setShowExitConfirm(false)} onStop={() => {
      setShowExitConfirm(false);
      onExit();
    }} data-id="element-6857" />
    </div>;
}